import { addPasskey, deletePasskey, signInWithPasskey } from "@/api/authNew";
import { useRouter } from "@/i18n/routing";
import { useCreatePasskeyStore } from "@/stores/CreatePasskeyStore";
import { passkeysQueryKeys } from "@/tanstackQuery/queries";
import { getAuthRedirectPath } from "@/utils/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";
import { publishSessionLogin } from "@/utils/sessionLogin";

const dismissPasskeyPrompt = (userId: number) => {
  const state = useCreatePasskeyStore.getState();
  state.dismiss(userId);
};

export function useAddPasskey() {
  const queryClient = useQueryClient();
  const t = useTranslations("passkey.errors");

  return useMutation({
    mutationFn: addPasskey,
    onSuccess() {
      toast.success(t("add_success"));
      queryClient.invalidateQueries({
        queryKey: [passkeysQueryKeys.list],
      });
    },
    onError(error: any) {
      const raw = error?.message ?? "";
      if (raw.includes("previously registered")) {
        toast.error(t("already_registered"));
      } else if (
        raw.includes("not allowed") ||
        error?.name === "NotAllowedError"
      ) {
        toast.error(t("cancelled"));
      } else {
        toast.error(t("generic"));
      }
    },
  });
}

export function useDeletePasskey() {
  const queryClient = useQueryClient();
  const t = useTranslations("passkey.errors");

  return useMutation({
    mutationFn: deletePasskey,
    onSuccess() {
      toast.success(t("delete_success"));
      queryClient.invalidateQueries({
        queryKey: [passkeysQueryKeys.list],
      });
    },
  });
}

export function usePasskeySignIn() {
  const locale = useLocale();
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signInWithPasskey,
    onSuccess(auth) {
      queryClient.clear();
      dismissPasskeyPrompt(auth.session.userId);
      // Sibling tabs parked on sign-in pick the new session up right away.
      publishSessionLogin({ now: new Date() });
      // Always land on the role's home page (no post-login redirect bounce).
      const entryPath = getAuthRedirectPath(auth);
      router.replace(entryPath, { locale });
      router.refresh();
    },
  });
}
